import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import {
  BrowserRouter,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router";
import "./index.css";
import App from "./App.tsx";
import "./dayjsConfig";
import * as Sentry from "@sentry/react";

import { I18nextProvider } from "react-i18next";
import { i18nInit } from "./i18n";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import "./pdfjs.ts";
import { ToastContainer } from "react-toastify";
import { HTTPError } from "ky";
import { handleApiErrorWithNotification } from "./helpers/errorHandling.ts";

// Define react-query Error interface to HTTPError from ky
declare module "@tanstack/react-query" {
  interface Register {
    defaultError: HTTPError;
  }
}

// Initialize Sentry
Sentry.init({
  release: __BUILD_VERSION__,
  dsn: "https://3e0ad6bdd731b78bd97baf8e2e8b6c9b@o1160602.ingest.us.sentry.io/4510437172248576",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.feedbackIntegration({
      // Add configuration if needed
      colorScheme: "light",
      autoInject: false, // We're using captureFeedback manually
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 0.25, // Do not capture all transactions, only 25%
  tracePropagationTargets: [
    "https://test.portal.mwork365.cloud",
    "https://staging.portal.mwork365.cloud",
    "https://portal.mwork365.com",
  ],
  environment: import.meta.env.MODE,
  enabled: import.meta.env.PROD, // Only enable in production
});

// TODO: handle log error
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // staleTime: DEFAULT_QUERY_STALE_TIME,
      // onError: (error) => handleApiError(error),
      retry: (count, error) => {
        if (!error) return true;

        // Max retry 3
        if (count >= 2) {
          handleApiErrorWithNotification(error);
          return false;
        }

        // Check if it's a Ky HTTPError with response
        if (!(error instanceof HTTPError) || !error.response) {
          // Network error, cancelled request, or non-HTTP error
          return false;
        }

        const status = error.response.status;

        return status >= 500 || status === 429;
      },
    },
    // We are currently dont have any mutations in app
    // mutations: {
    //   // onError: (error) => handleApiError(error),
    //   retry: (count, error) => {
    //     if (!error) return true;

    //     // Max retry 3
    //     if (count >= 2) return false;

    //     const status = error.response.status;
    //     return status >= 500 || status === 429;
    //   },
    // },
  },
});

const emotionCache = createCache({ key: "css", prepend: true });

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <CacheProvider value={emotionCache}>
      <I18nextProvider i18n={i18nInit}>
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            <App />
            <ToastContainer />
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </BrowserRouter>
      </I18nextProvider>
    </CacheProvider>
  </StrictMode>,
);
